import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { StoreVisibilityPanel } from "@/components/admin/StoreVisibilityPanel";

const BUSINESS_TYPE_LABEL = {
  UNCLASSIFIED: "Unclassified",
  RETAIL: "Retail / Commerce",
  SAAS_DIGITAL_SERVICES: "SaaS / Digital Services",
};

const USD_SETTLEMENT_LABEL = {
  READY:             { label: "READY",             cls: "bg-slate-100 text-slate-700",  icon: Clock },
  INVOICE_CREATING:  { label: "INVOICING",         cls: "bg-blue-50 text-blue-800",     icon: Loader2 },
  INVOICED:          { label: "INVOICED",          cls: "bg-emerald-50 text-emerald-800", icon: CheckCircle2 },
  FAILED_RETRYABLE:  { label: "RETRY PENDING",     cls: "bg-amber-50 text-amber-800",   icon: AlertTriangle },
  FAILED_TERMINAL:   { label: "TERMINAL FAILURE",  cls: "bg-rose-50 text-rose-800",     icon: AlertTriangle },
  AMBIGUOUS:         { label: "AMBIGUOUS · REVIEW", cls: "bg-purple-50 text-purple-800", icon: AlertTriangle },
};

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [usdSettlementsByStore, setUsdSettlementsByStore] = useState({});
  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/stores${q}`).then((r) => setStores(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Fetch latest USD settlement status per store visible in the table.
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/billing/usd-settlements")
      .then((r) => {
        if (cancelled) return;
        const latest = {};
        for (const s of (r.data?.settlements || [])) {
          // Rows are already sorted newest-first by the server.
          if (!latest[s.store_id]) latest[s.store_id] = s;
        }
        setUsdSettlementsByStore(latest);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [stores.length]);

  const approve = async (id) => { await api.post(`/admin/stores/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/stores/${id}/reject`); toast.success("Rejected"); load(); };

  return (
    <div data-testid="admin-stores">
      <StoreVisibilityPanel />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">Stores</h2>
          <p className="text-sm text-muted-foreground mt-1">Review and approve merchant listings.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest border ${filter === f ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`} data-testid={`filter-store-${f}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="pf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-5 py-3">Store</th>
              <th className="px-5 py-3">Slug</th>
              <th className="px-5 py-3">Website</th>
              <th className="px-5 py-3">Business Type</th>
              <th className="px-5 py-3">Currencies</th>
              <th className="px-5 py-3">Latest USD Settlement</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`store-row-${s.id}`}>
                <td className="px-5 py-3 font-medium flex items-center gap-2">
                  {s.logo_url && <img src={s.logo_url} alt="" className="w-8 h-8 rounded border border-border" />}
                  {s.name}
                </td>
                <td className="px-5 py-3 font-mono text-xs">{s.slug}</td>
                <td className="px-5 py-3 text-xs">{s.website || "—"}</td>
                <td className="px-5 py-3 text-xs" data-testid={`store-business-type-${s.id}`}>
                  <span
                    className={`pf-chip ${
                      (s.merchant_type || "UNCLASSIFIED") === "UNCLASSIFIED"
                        ? "bg-secondary text-slate-600"
                        : "bg-cyan-50 text-cyan-800"
                    }`}
                  >
                    {BUSINESS_TYPE_LABEL[s.merchant_type || "UNCLASSIFIED"]}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs" data-testid={`store-currencies-${s.id}`}>
                  {(s.supported_checkout_currencies || []).length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(s.supported_checkout_currencies || []).map((c) => (
                        <span
                          key={c}
                          className={`pf-chip ${
                            c === s.primary_checkout_currency
                              ? "bg-slate-900 text-white"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-xs" data-testid={`store-latest-usd-${s.id}`}>
                  {(() => {
                    const settle = usdSettlementsByStore[s.id];
                    if (!settle) return <span className="text-slate-400">—</span>;
                    const meta = USD_SETTLEMENT_LABEL[settle.status] || USD_SETTLEMENT_LABEL.READY;
                    const Icon = meta.icon;
                    return (
                      <div className="flex flex-col gap-0.5">
                        <span className={`inline-flex items-center gap-1 pf-chip ${meta.cls}`}>
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {settle.stripe_invoice_id || settle.id?.slice(0, 8)}
                        </span>
                        {settle.failure_reason && (
                          <span className="text-[10px] text-rose-600 max-w-[220px] truncate"
                                title={settle.failure_reason}>
                            {settle.failure_reason}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-5 py-3">
                  <span className={`pf-chip ${s.status === "approved" ? "bg-[color:var(--pf-mint)] text-black" : s.status === "rejected" ? "bg-destructive text-white" : "bg-secondary"}`}>{s.status}</span>
                </td>
                <td className="px-5 py-3 text-right space-x-1">
                  {s.status !== "approved" && <button onClick={() => approve(s.id)} className="text-xs font-semibold px-3 py-1 rounded-full bg-foreground text-background" data-testid={`approve-store-${s.id}`}>Approve</button>}
                  {s.status !== "rejected" && <button onClick={() => reject(s.id)} className="text-xs font-semibold px-3 py-1 rounded-full border border-border" data-testid={`reject-store-${s.id}`}>Reject</button>}
                </td>
              </tr>
            ))}
            {stores.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-muted-foreground">No stores.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
